/**
 * Distribuzione dei pagamenti al consulente sulle righe di provvigione.
 *
 * Perché esiste (12/08/2026, segnalazione Linda su MOVEO SRL - offerta 048/2026):
 * `commission_consultant_payments` registra il pagamento per (offerta, servizio a CATALOGO,
 * variabile, persona, mese), non per riga di servizio dell'offerta. Quando un'offerta ha più
 * righe che puntano allo stesso servizio a catalogo - qui due consulenze "VARIE" da 600 e 500 -
 * tutte le righe cadono nella stessa chiave. La pagina Provvigioni assegnava il totale INTERO a
 * ciascuna riga: 880 € liquidati venivano mostrati come 880 su entrambe, cioè 1.760 € a fronte
 * di 880 € dovuti, e la colonna "Provv. Pagabile" andava a zero su tutte e due.
 *
 * La regola qui è la stessa che il motore applica già lato server in
 * `flusia-core/src/domains/commissions/projection/paymentAllocation.ts`: il totale liquidato si
 * consuma sulle righe del gruppo, dalla competenza più vecchia, ciascuna fino al proprio dovuto.
 * L'eventuale eccedenza resta sull'ultima riga e la marca `overpaid`: una liquidazione superiore
 * al dovuto deve restare visibile, non va assorbita in silenzio.
 *
 * Nota: questo è un rimedio a valle. La causa a monte è che la tabella dei pagamenti non ha
 * `deal_service_id`, quindi l'informazione per attribuire il pagamento alla riga esatta non è
 * proprio registrata. Finché resta così, anche i pagamenti già esistenti si possono solo
 * ripartire, non attribuire.
 */

export interface RigaProvvigione {
  deal_id: string;
  service_id?: string | null;
  variable_id?: string | null;
  deal_service_id?: string | null;
  person_id: string;
  is_agent: boolean;
  month_index?: number | null;
  calculation_date?: string | null;
  commission_amount: number;
}

export interface QuotaPagata {
  pagato: number;
  stato: 'partial' | 'full' | 'overpaid';
}

const TOLLERANZA = 0.01;

/** Chiave con cui i pagamenti sono raggruppati: deve combaciare con quella usata a monte. */
export function chiavePagamento(riga: RigaProvvigione): string {
  return [
    riga.deal_id,
    riga.service_id ?? 'null',
    riga.variable_id ?? 'null',
    riga.person_id,
    riga.is_agent ? 'agent' : 'referrer',
    riga.month_index ?? 'null',
  ].join('_');
}

/**
 * Ritorna, per indice di riga nell'array in ingresso, la quota di liquidato che le compete.
 * Le righe senza pagamenti sul proprio gruppo non compaiono nella mappa.
 */
export function distribuisciPagatoSulleRighe(
  righe: RigaProvvigione[],
  pagamentiPerChiave: Map<string, { total: number; count: number }>,
): Map<number, QuotaPagata> {
  const quote = new Map<number, QuotaPagata>();
  const gruppi = new Map<string, number[]>();

  righe.forEach((riga, indice) => {
    const chiave = chiavePagamento(riga);
    if (!pagamentiPerChiave.has(chiave)) return;
    const gruppo = gruppi.get(chiave);
    if (gruppo) gruppo.push(indice);
    else gruppi.set(chiave, [indice]);
  });

  for (const [chiave, indici] of gruppi) {
    let residuo = pagamentiPerChiave.get(chiave)?.total ?? 0;
    if (residuo <= 0) continue;

    // Ordine deterministico: competenza crescente, poi mese, poi riga di servizio. Serve a non
    // far ballare l'attribuzione fra un caricamento e l'altro quando le righe pareggiano.
    const ordinati = [...indici].sort((a, b) => {
      const ra = righe[a]!;
      const rb = righe[b]!;
      const da = ra.calculation_date ?? '';
      const db = rb.calculation_date ?? '';
      if (da !== db) return da < db ? -1 : 1;
      const ma = ra.month_index ?? 0;
      const mb = rb.month_index ?? 0;
      if (ma !== mb) return ma - mb;
      return (ra.deal_service_id ?? '').localeCompare(rb.deal_service_id ?? '');
    });

    for (let i = 0; i < ordinati.length; i++) {
      const indice = ordinati[i]!;
      const dovuto = Math.max(0, righe[indice]!.commission_amount || 0);
      const ultima = i === ordinati.length - 1;
      const quota = ultima ? residuo : Math.min(residuo, dovuto);
      if (quota > 0) {
        quote.set(indice, {
          pagato: Math.round(quota * 100) / 100,
          stato: quota > dovuto + TOLLERANZA ? 'overpaid' : quota >= dovuto - TOLLERANZA ? 'full' : 'partial',
        });
      }
      residuo -= quota;
      if (residuo <= 0) break;
    }
  }

  return quote;
}
